import { OrderAdjustmentsTypes } from 'constants/orderAdjustmentTypes';

import { CreateOrderAdjustmentResponse } from 'config/requests/orderAdjustments';
import { useCreateOrderAdjustments } from 'pages/OrderAdjustments/hooks/useCreateOrderAdjustments';
import { OrderAdjustmentsFormValues } from 'pages/OrderAdjustments/hooks/useOrderAdjustmentsForm';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useListContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Button, CancelButton } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { DialogProps } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Typography } from 'shared/mui/Typography';
import { getId } from 'utils';
import { cleanEmpty } from 'utils/cleanEmpty';

import { StyledBox, StyledDialog } from './OrderAdjustmentsSubmitDialog.styled';

type Props = DialogProps & { onSuccess: (result: CreateOrderAdjustmentResponse) => void };

export const OrderAdjustmentsSubmitDialog = ({ open, onClose, onSuccess }: Props) => {
  const translate = useTypedTranslate();
  const { getValues, handleSubmit } = useFormContext<OrderAdjustmentsFormValues>();
  const { createOrderAdjustments } = useCreateOrderAdjustments(onSuccess);
  const { selectedIds } = useListContext();

  const submitHandler = () => {
    const data = getValues();

    createOrderAdjustments.mutate({
      ...data,
      adjustmentType: getId(data.adjustmentType as string) as OrderAdjustmentsTypes,
      orderAdjustments: data.orderAdjustments
        .filter((adjustment) => selectedIds.includes(adjustment.orderId))
        .map((adjustment) =>
          cleanEmpty({
            ...adjustment,
            orderId: getId(adjustment.orderId),
          }),
        ),
    });

    onClose();
  };

  return (
    <StyledDialog maxWidth="md" onClose={onClose} open={open}>
      <form onSubmit={handleSubmit(submitHandler as any)}>
        <StyledBox>
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            title={translate('app.orderAdjustments.submitDialog.title')}
            titleSize="smallBold"
          />
          <CardContent>
            <Typography>{translate('app.orderAdjustments.submitDialog.message')}</Typography>
          </CardContent>
          <CardActionsButtons
            rightActionsSlot={[
              <CancelButton
                key="cancel-button"
                label={translate('app.orderAdjustments.submitDialog.cancel')}
                onClick={onClose}
                variant="outlined"
              />,
              <Button
                key="save-button"
                label={translate('app.orderAdjustments.submitDialog.confirm')}
                type="submit"
                variant="contained"
              />,
            ]}
          />
        </StyledBox>
      </form>
    </StyledDialog>
  );
};
